import classNames from 'classnames';
import React, { useState } from 'react';
import styles from './Card.module.scss';

/**
 * Local reimplementation of `@eventbrite/eds-card`'s `Card` (the exact subset
 * this package uses: `style="neutral"` + `containerClassName`).
 *
 * Why reimplemented: the EDS catalog (out/eds-catalog) lists `eds-card` as
 * UNRESOLVED — there is NO `legacy-eds.card` bridge (E404) and NO Marmalade
 * `Card` (Marmalade's `ChoiceCard`/`Container` are different components). The
 * catalog's suggested resolution is a local reimpl from the eds source, which
 * this is: markup (`role="presentation"`, focus/blur tracking) and the
 * `.eds-card`/`.eds-card--neutral`/`--focused`/`--selected`/`--raised` styles
 * are ported 1:1 from `eb-ui/packages/eds/eds-card/src/Card.jsx` + `card.scss`.
 */

export const CARD_STYLE_SIMPLE = 'simple';
export const CARD_STYLE_NEUTRAL = 'neutral';
export type CardStyle = typeof CARD_STYLE_SIMPLE | typeof CARD_STYLE_NEUTRAL;

export interface CardProps {
    children: React.ReactNode;
    /** Changes the style of the card. Available styles: simple, neutral. */
    style?: CardStyle;
    /** Changes the style of a selected card. */
    isSelected?: boolean;
    /** Optional hook to inject classes onto the base div element. */
    containerClassName?: string;
    /** Lifts the card akin to focus or hover. */
    isRaised?: boolean;
}

export const Card: React.FC<CardProps> = ({
    children,
    style,
    isSelected,
    containerClassName,
    isRaised,
}) => {
    const [focused, setFocused] = useState(false);

    const className = classNames(styles['eds-card'], {
        [styles[`eds-card--${style}`]]: !!style,
        [styles['eds-card--focused']]: focused,
        [styles['eds-card--selected']]: !!isSelected,
        [styles['eds-card--raised']]: !!isRaised,
        [containerClassName as string]: !!containerClassName,
    });

    return (
        <div
            className={className}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            role="presentation"
        >
            {children}
        </div>
    );
};

export default Card;
